import { getLocation } from "@core/geolocation";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { Input, Modal, Select, message as antdMessage } from "antd";
import { Loader, MapPin, X } from "lucide-react";

import { addAddress, getAllStates, getCitiesOfState } from "@api/address";
import { createAddress, createAddressV1, createUser, getUserLocation } from "@api/visitors";
import {
    TAddress,
    TCities,
    TData,
    TGeoLocation,
    TIdentity,
    TOption,
    TStates,
    TStore,
    TVisitor,
} from "@components/types";
import { getFilteredResults, requestLogin } from "@core/utils";
import { addressState, isLoggedIn, profileState } from "@recoil/atoms";

type MapPosition = {
    lat: number;
    lng: number;
};

const AddressLeafletMap = dynamic(() => import("./leafletmap"), {
    ssr: false,
});

interface AddAddressProps {
    addressCallback: Function;
    close: Function;
    initialAddress: TAddress;
    storeAddress?: TAddress;
    isEdit: Boolean;
    setSuccess?: React.Dispatch<React.SetStateAction<boolean>>;
    store?: TStore
}

export default function AddAddress({
    addressCallback,
    close,
    initialAddress,
    storeAddress,
    isEdit,
    setSuccess,
    store,
}: AddAddressProps) {
    const [address, setAddress] = useState<TAddress>({ state_id: storeAddress?.state_id, city_id: storeAddress?.city_id });
    const [states, setStates] = useState<TStates[]>();
    const [cities, setCities] = useState<TCities[]>();
    const [citySuggestions, setCitySuggestions] = useState<TOption[]>([]);
    const [stateSuggestions, setStateSuggestions] = useState<TOption[]>([]);
    const [visitorInfo, setVisitorInfo] = useState<TVisitor | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [message, setMessage] = useState("");
    const [mapCenter, setMapCenter] = useState<MapPosition>({ lat: 28.6139, lng: 77.209 });
    const [pendingMapPosition, setPendingMapPosition] = useState<MapPosition>({ lat: 28.6139, lng: 77.209 });
    const [isMapReady, setIsMapReady] = useState(false);
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    const isLogin = useRecoilValue(isLoggedIn);
    const user = useRecoilValue<TIdentity>(profileState);
    const [selectedAddress, setSelectedAddress] = useRecoilState(addressState);
    const hasCenteredMapRef = useRef(false);
    // Add error states for validation
    const [errors, setErrors] = useState({
        house_number: "",
        street_name: "",
        locality: ""
    });

    const router = useRouter();

    const handleStateChange = (
        name: string,
        data: TData[],
        callback: Function
    ) => {
        const handler = (value: string) => {
            let state = data.filter((state) => state.id === parseInt(value));
            if (state.length > 0) {
                callback({ ...address, [name]: state[0].id });
            }
        };
        return handler;
    };

    const handleCityChange = (
        name: string,
        data: TData[],
        callback: Function
    ) => {
        const handler = (value: string) => {
            let city = data.filter((city) => city.id === parseInt(value));
            if (city.length > 0) {
                callback({ ...address, [name]: city[0].id });
            }
        };
        return handler;
    };

    const handleStateSearch = (value: string) => {
        setStateSuggestions(getFilteredResults(value, states));
    };

    const handleCitySearch = (value: string) => {
        if (address?.state_id != undefined) {
            getCitiesOfState(value, address?.state_id).then((result) => {
                setCities(result);
                setCitySuggestions(getFilteredResults(value, result));
            });
        }
    };

    const handleInput = async (evt: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = evt.target;
        setErrors({
            ...errors,
            [name]: ""
        });

        if (name === "house_number" && value?.length >= 50) {
            setErrors({
                ...errors,
                house_number: "House/Shop Number cannot exceed 50 characters"
            });
        } else if (name === "street_name" && value?.length >= 400) {
            setErrors({
                ...errors,
                street_name: "Street name cannot exceed 400 characters"
            });
        } else if (name === "locality" && value?.length >= 400) {
            setErrors({
                ...errors,
                locality: "Locality cannot exceed 400 characters"
            });
        }
        setAddress({ ...address, [name]: value });
    };

    const checkStale = (location: TGeoLocation) => {
        return Date.now() - location.last_updated > 60000;
    };

    const reverseFillAddress = useCallback((latitude: number, longitude: number, successMessage = "Location saved") => {
        setIsLoadingLocation(true);
        return getUserLocation(longitude, latitude, store?.id)
            .then((currentLocation) => {
                if (currentLocation?.city?.name && currentLocation?.state_id) {
                    getCitiesOfState(
                        currentLocation?.city?.name,
                        currentLocation?.state_id
                    ).then((cities) => {
                        setCities(cities);
                    });
                }

                setAddress((prevAddress) => ({
                    ...prevAddress,
                    house_number: currentLocation?.house_number || "",
                    street_name: currentLocation?.street_name || "",
                    locality: currentLocation?.locality || "",
                    state_id: currentLocation?.state_id,
                    city_id: currentLocation?.city_id,
                    pincode: currentLocation?.pincode,
                    latitude,
                    longitude
                }));
                setMapCenter({ lat: latitude, lng: longitude });
                antdMessage.success(successMessage);
            })
            .catch(() => {
                antdMessage.error("Oops! something went wrong.");
            })
            .finally(() => {
                setIsLoadingLocation(false);
            });
    }, [store?.id]);

    const updateMarkerPosition = useCallback((position: MapPosition, shouldReverseFill = false) => {
        setMapCenter(position);
        setPendingMapPosition(position);

        if (shouldReverseFill) {
            reverseFillAddress(position.lat, position.lng, "Location selected");
        }
    }, [reverseFillAddress]);

    const openMapModal = () => {
        const currentPosition = {
            lat: address?.latitude || mapCenter.lat,
            lng: address?.longitude || mapCenter.lng,
        };

        setIsMapModalOpen(true);
        setIsMapReady(false);
        setMapCenter(currentPosition);
        setPendingMapPosition(currentPosition);

        getLocation(true)
            .then((location) => {
                if (location?.latitude > 0 && location?.longitude > 0 && !(checkStale(location) && location?.code == 2)) {
                    updateMarkerPosition({ lat: location.latitude, lng: location.longitude }, false);
                }
            })
            .catch(() => { });
    };

    const closeMapModal = () => {
        setIsMapModalOpen(false);
    };

    const confirmPinnedLocation = () => {
        reverseFillAddress(pendingMapPosition.lat, pendingMapPosition.lng, "Location selected")
            .then(() => {
                setIsMapModalOpen(false);
            });
    };

    useEffect(() => {
        console.log(isLogin);
    }, [isLogin]);

    const handleAddAddress = async () => {
        if (!(address?.latitude > 0 && address?.longitude > 0)) {
            antdMessage.warning("Please mark your location on the map to get correct delivery.");
            return;
        }

        if (visitorInfo) {
            setIsLoading(true);
            try {
                console.log(isLogin)
                if (isLogin) {
                    const newAddress = await createAddress({
                        ...address,
                        user_id: user?.id,
                    });
                    if (newAddress) {
                        setSuccess && setSuccess(true);
                        close();
                    }
                } else {
                    if (visitorInfo !== undefined && visitorInfo !== null) {
                        const newUser = await createUser(
                            visitorInfo?.name,
                            visitorInfo?.phone
                        );
                        if (newUser?.id) {
                            const newAddress = await createAddress({
                                ...address,
                                user_id: newUser?.id,
                            });
                            if (newAddress) {
                                localStorage.setItem("profile", JSON.stringify(newUser));
                                setSelectedAddress(newAddress);
                                setSuccess && setSuccess(true);
                                close();
                            }
                        }
                    }
                }
            } catch (error) {
                antdMessage.error("Oops! something went wrong.");
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsLoading(true);
            if (isLogin) {
                await addAddress(address)?.then((res) => {
                    setMessage("Address submitted");
                    addressCallback(res);
                }).catch((e) => {
                    if (e.response?.status === 401) {
                        requestLogin(router.asPath);
                    } else {
                        antdMessage.error("Oops! something went wrong.");
                    }
                }).finally(() => {
                    setIsLoading(false);
                    close();
                });
            } else {
                await createAddressV1({
                    ...address,
                }).then((res) => {
                    setSelectedAddress(res);
                    setSuccess && setSuccess(true);
                    addressCallback(res);
                }).finally(() => {
                    setIsLoading(false);
                    close();
                });
            }
        }
    };

    useEffect(() => {
        if (initialAddress) {
            setAddress(initialAddress);
        } else {
            setAddress({ state_id: storeAddress?.state_id, city_id: storeAddress?.city_id })
        }
    }, [initialAddress, storeAddress]);

    useEffect(() => {
        if (window && localStorage.getItem("visitorInfo")) {
            setVisitorInfo(JSON.parse(localStorage.getItem("visitorInfo")));
        }
    }, []);

    useEffect(() => {
        setStateSuggestions(getFilteredResults(undefined, states));
        setCitySuggestions(getFilteredResults(undefined, cities));
    }, [states, cities]);

    useEffect(() => {
        const res: Promise<TStates[]> = getAllStates();
        res.then((states) => setStates(states));
    }, []);

    useEffect(() => {
        if (address?.state_id != undefined) {
            const res: Promise<TCities[]> = getCitiesOfState(null, address?.state_id);
            res.then((cities) => setCities(cities));
        }
    }, [address?.state_id]);

    useEffect(() => {
        const initialLat = address?.latitude || storeAddress?.latitude || storeAddress?.city?.latitude;
        const initialLng = address?.longitude || storeAddress?.longitude || storeAddress?.city?.longitude;

        if (initialLat && initialLng) {
            const initialPosition = { lat: initialLat, lng: initialLng };
            setMapCenter(initialPosition);
            setPendingMapPosition(initialPosition);
        }
    }, [address?.latitude, address?.longitude, storeAddress]);

    useEffect(() => {
        if (hasCenteredMapRef.current || isEdit) {
            return;
        }

        hasCenteredMapRef.current = true;
        getLocation(true)
            .then((location) => {
                if (location?.latitude > 0 && location?.longitude > 0 && !(checkStale(location) && location?.code == 2)) {
                    const currentPosition = { lat: location.latitude, lng: location.longitude };
                    setMapCenter(currentPosition);
                    setPendingMapPosition(currentPosition);
                    setAddress((prevAddress) => ({
                        ...prevAddress,
                        latitude: location.latitude,
                        longitude: location.longitude,
                    }));
                }
            })
            .catch(() => { });
    }, [isEdit]);

    return (
        <div className="flex-1 h-fit p-4 md:p-6 relative font-manrope rounded-lg border border-solid border-neutral-200 bg-neutral-50">
            <button
                title="Close"
                type="button"
                onClick={() => close()}
                className="px-0 absolute top-4 right-4 rounded-full cursor-pointer"
            >
                <X className="w-5 h-5 text-neutral-800" />
            </button>

            <div className="space-y-4">
                <h6 className="text-base md:text-lg text-neutral-800 font-bold">
                    {isEdit ? 'Edit Address' : 'Add Address'}
                </h6>

                <div className="space-y-2.5 md:space-y-4">
                    <div>
                        <button
                            type="button"
                            onClick={openMapModal}
                            className="w-full h-10 flex justify-center items-center gap-2 rounded-md bg-brand-500 cursor-pointer"
                        >
                            <MapPin className="w-4 h-4 text-white" />
                            <span className="text-sm text-white font-manrope font-semibold">
                                Mark on Map
                            </span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        <div className="flex flex-col gap-1">
                            <label className="flex justify-start font-semibold text-sm text-gray-600">
                                House Number
                            </label>
                            <Input
                                size="middle"
                                name="house_number"
                                placeholder="House Number"
                                value={address?.house_number}
                                onChange={(evt) => handleInput(evt)}
                                maxLength={50}
                            />
                            {errors.house_number && <p className="text-red-500 text-xs mt-1">{errors.house_number}</p>}
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="flex justify-start font-semibold text-sm text-gray-600">
                                Address Type
                            </label>
                            <Select
                                value={address?.type ? address?.type : 'home'}
                                onChange={(type) => setAddress({ ...address, type: type })}
                                options={[
                                    { value: 'home', label: 'Home' },
                                    { value: 'work', label: 'Work' },
                                    { value: 'other', label: 'Other' },
                                ]}
                                popupMatchSelectWidth={false}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="flex justify-start font-semibold text-sm text-gray-600">
                            Street Name
                        </label>
                        <Input
                            size="middle"
                            name="street_name"
                            maxLength={400}
                            placeholder="Street Name"
                            value={address?.street_name}
                            onChange={(evt) => handleInput(evt)}
                        />
                        {errors.street_name && <p className="text-red-500 text-xs mt-1">{errors.street_name}</p>}
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="flex justify-start font-semibold text-sm text-gray-600">
                            Locality
                        </label>
                        <Input
                            size="middle"
                            name="locality"
                            maxLength={400}
                            placeholder="Locality"
                            value={address?.locality}
                            onChange={(evt) => handleInput(evt)}
                        />
                        {errors.locality && <p className="text-red-500 text-xs mt-1">{errors.locality}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        <div className="flex flex-col gap-1">
                            <label className="flex justify-start font-semibold text-sm text-gray-600">
                                State
                            </label>
                            <Select
                                size="middle"
                                showSearch
                                className="w-full"
                                value={address?.state_id?.toString()}
                                onChange={handleStateChange("state_id", states, setAddress)}
                                onSearch={handleStateSearch}
                                filterOption={false}
                                popupMatchSelectWidth={false}
                                options={stateSuggestions}
                                placeholder="Select State"
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="flex justify-start font-semibold text-sm text-gray-600">
                                City
                            </label>
                            <Select
                                size="middle"
                                className="w-full"
                                disabled={address?.state_id === undefined}
                                showSearch
                                value={address?.city_id?.toString()}
                                onChange={handleCityChange("city_id", cities, setAddress)}
                                onSearch={handleCitySearch}
                                filterOption={false}
                                popupMatchSelectWidth={false}
                                options={citySuggestions}
                                placeholder="Select City"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="flex justify-start font-semibold text-sm text-gray-600">
                            Pincode
                        </label>
                        <Input
                            size="middle"
                            name="pincode"
                            placeholder="Pincode"
                            value={address?.pincode}
                            onChange={(evt) => handleInput(evt)}
                        />
                    </div>

                    {message && (
                        <div>
                            <span className="text-sm text-error-500 font-semibold">
                                {message}
                            </span>
                        </div>
                    )}

                    <div className="flex items-center gap-2.5">
                        <button
                            type="button"
                            onClick={handleAddAddress}
                            disabled={isLoading}
                            className="w-fit h-10 px-4 flex items-center gap-2 transition duration-200 rounded-md bg-brand-500 hover:opacity-80 cursor-pointer disabled:cursor-not-progress disabled:opacity-80"
                        >
                            {isLoading && (
                                <Loader className="w-4 h-4 text-white animate-spin" />
                            )}
                            <span className="text-xs text-white font-manrope font-semibold tracking-wider uppercase">
                                {isLoading ? "Loading..." : isEdit ? "Save Address" : "Create Address"}
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={() => close()}
                            className="w-fit h-10 px-4 flex items-center gap-2 rounded-md border border-solid border-brand-500 cursor-pointer"
                        >
                            <span className="text-xs text-brand-500 font-manrope font-semibold tracking-wider uppercase">
                                Cancel
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <Modal
                title="Mark delivery location"
                open={isMapModalOpen}
                onCancel={closeMapModal}
                footer={null}
                width={720}
                centered
                destroyOnClose
            >
                <div className="space-y-3">
                    <p className="text-sm text-neutral-600">
                        Tap the map or drag the pin to the exact location, then save it.
                    </p>

                    <div className="relative h-[360px] md:h-[420px] overflow-hidden rounded-lg border border-solid border-neutral-200 bg-neutral-100">
                        {isMapModalOpen && (
                            <AddressLeafletMap
                                center={mapCenter}
                                position={pendingMapPosition}
                                onChange={(position) => {
                                    setMapCenter(position);
                                    setPendingMapPosition(position);
                                }}
                                onReady={() => setIsMapReady(true)}
                            />
                        )}
                        {!isMapReady && (
                            <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                                <Loader className="w-5 h-5 text-brand-500 animate-spin" />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <span className="text-xs text-neutral-500">
                            Selected: {pendingMapPosition.lat.toFixed(6)}, {pendingMapPosition.lng.toFixed(6)}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={closeMapModal}
                                className="h-10 px-4 rounded-md border border-solid border-neutral-300 text-sm font-semibold text-neutral-700 cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmPinnedLocation}
                                disabled={isLoadingLocation}
                                className="h-10 px-4 flex items-center gap-2 rounded-md bg-brand-500 text-sm font-semibold text-white cursor-pointer disabled:opacity-80"
                            >
                                {isLoadingLocation && <Loader className="w-4 h-4 text-white animate-spin" />}
                                Save Location
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
